import React, { Component } from "react";

import ViewCartLink from "./ViewCartLink";
import EndUserDownloadLink from "./EndUserDownloadLink";
import DownloadWithEmailButton from "./DownloadWithEmailButton";
import AddToCartButton from "./AddToCartButton";
import RemoveFromCartButton from "./RemoveFromCartButton";
import CurationToggleButton from "./CurationToggleButton";
import AdminDownloadSaveSection from "./AdminDownloadSaveSection";
import { OrgDownloadContext } from "../../../OrganizationPhotos/OrgDownloadContext";
import { Event } from "../../../OrganizationPhotos/types";
import { securedEventPath } from "../../../../utils/pathUtils";
import ContrastForegroundUtility from "../../../../utils/ContrastForegroundUtility";

interface Photo {
  id: string;
  isSelected: boolean;
  fileName: string;
  digitalDownloadDisabled?: boolean;
}

interface Props {
  cartHasPhotos: boolean;
  freeEvent: boolean;
  cartId: number;
  parentName: string;
  primaryColor: string;
  userId: string | null;
  href: string;
  photo: Photo;
  addToCart: (photo: Photo) => void;
  removeFromCart: (photo: Photo) => void;
  handleDownloadEmailModal: () => void;
  curationMode: boolean;
  photoHidden: boolean;
  curatePhoto: () => void;
  setCurationApiCalled: (state: boolean) => void;
  photoSaved: boolean;
  handleSave: () => void;
  handleUnsave: () => void;
  handleToggle: (id: string, msg: string) => void;
  handleHideTooltip: () => void;
  canDownloadPhotos: boolean;
  isEmbed: boolean;
  eventData: Event;
  isPrepaid: boolean;
  securedGalleryView: boolean;
  directDownloadEnabled: boolean;
  endUserDownloadsDisabled: boolean;
}

class LightBoxFooterActions extends React.Component<Props> {
  static contextType = OrgDownloadContext;

  eventPath = (event: Event) => {
    return securedEventPath({
      browsePhotosPath: event.browsePhotosPath,
      organizerUserName: this.context.organizerUserName,
      embedMode: this.props.isEmbed,
      cartId: this.props.cartId,
      secureAccessToken: this.context.secureAccessToken || undefined
    });
  };

  viewEventGalleryBtnStyles = {
    color: this.props.primaryColor
      ? ContrastForegroundUtility.fgColor(this.props.primaryColor)
      : "#fff",
    background: this.props.primaryColor || "#ff0072",
    height: "fit-content"
  };
  render() {
    const {
      cartHasPhotos,
      freeEvent,
      cartId,
      isEmbed,
      parentName,
      primaryColor,
      userId,
      href,
      photo,
      addToCart,
      removeFromCart,
      handleDownloadEmailModal,
      curationMode,
      photoHidden,
      curatePhoto,
      setCurationApiCalled,
      photoSaved,
      handleSave,
      handleUnsave,
      handleToggle,
      handleHideTooltip,
      canDownloadPhotos,
      eventData,
      isPrepaid,
      securedGalleryView
    } = this.props;

    const isSecuredFaceSearch = this.context?.fromSecuredFaceSearch;

    const directDownloadEnabled =
      this.context?.directDownloadEnabled || this.props.directDownloadEnabled;

    const endUserDownloadsDisabled =
      freeEvent && ((this.context as any)?.endUserDownloadsDisabled || this.props.endUserDownloadsDisabled);

    const renderPhotoInteractionSection = () => {
      if (endUserDownloadsDisabled) {
        return null;
      }
      if (!eventData.hasPrintProducts && photo.digitalDownloadDisabled) {
        return null;
      }
      if (freeEvent && directDownloadEnabled) {
        return userId || securedGalleryView ? (
          <EndUserDownloadLink href={href} primaryColor={primaryColor} />
        ) : (
          <DownloadWithEmailButton
            primaryColor={primaryColor}
            handleClick={handleDownloadEmailModal}
          />
        );
      }

      if (isSecuredFaceSearch && directDownloadEnabled) {
        const linkProps = securedGalleryView
          ? { target: "_blank", rel: "noopener noreferrer" }
          : {};

        return (
          <a
            style={this.viewEventGalleryBtnStyles}
            className="geo-btn geo-btn--caps"
            href={this.eventPath(eventData)}
            {...linkProps}
          >
            {securedGalleryView ? (
              <span>
                Purchase from event
                <span className="fa fa-angle-right geo-eve-name-arrow" />
              </span>
            ) : (
              "View Event Gallery"
            )}
          </a>
        );
      }

      return (
        <div className="dv-view-add-div">
          {photo.isSelected && (
            <ViewCartLink
              cartId={cartId}
              isEmbed={isEmbed}
              parentName={parentName}
              primaryColor={primaryColor}
            />
          )}
          {photo.isSelected ? (
            <RemoveFromCartButton
              primaryColor={primaryColor}
              handleClick={() => removeFromCart(photo)}
            />
          ) : (
            <AddToCartButton
              primaryColor={primaryColor}
              handleClick={() => addToCart(photo)}
            />
          )}
        </div>
      );
    };

    const isAuthorizedForDirectDownloads = canDownloadPhotos;

    const renderPricingDetails = () => {
      if (endUserDownloadsDisabled) {
        return null;
      }
      if (freeEvent) {
        return <h3 style={{ textTransform: "uppercase" }}>Free</h3>;
      } else if (isPrepaid) {
        return (
          <div className="prepaid">
            <del>{eventData.displayPrice} </del>
            <span className="currency">{eventData.currency}</span>
            <span className="free"> Free </span>
          </div>
        );
      } else {
        return (
          <h3>
            {eventData.displayPrice} <span>{eventData.currency}</span>
          </h3>
        );
      }
    };

    return (
      <>
        <div className="modal-footer-details">
          {renderPricingDetails()}
          {!endUserDownloadsDisabled && (
            <p className="geo-lt__info">*for High quality digital download</p>
          )}
          {photo.fileName && (
            <p className="modal-file-name" style={{ paddingLeft: 0 }}>
              {photo.fileName}
            </p>
          )}
        </div>
        <div className="modal-footer-actions eg-lt-footer">
          {renderPhotoInteractionSection()}
          {curationMode && (
            <CurationToggleButton
              photoHidden={photoHidden}
              primaryColor={primaryColor}
              handleClick={() => {
                curatePhoto();
                setCurationApiCalled(true);
              }}
              handleToggle={handleToggle}
              handleHideTooltip={handleHideTooltip}
            />
          )}
          {isAuthorizedForDirectDownloads && (
            <AdminDownloadSaveSection
              photo={photo}
              photoSaved={photoSaved}
              primaryColor={primaryColor}
              handleSave={handleSave}
              handleUnsave={handleUnsave}
              handleToggle={handleToggle}
              handleHideTooltip={handleHideTooltip}
              userId={userId}
              cartId={cartId}
              isEmbed={isEmbed}
            />
          )}
        </div>
      </>
    );
  }
}

export default LightBoxFooterActions;
